import React, { useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';

import { gql, useQuery, useMutation } from '@apollo/client';

import Lottie from 'react-lottie-player';
import { toast } from 'react-toastify';
import _ from 'lodash';
import * as Yup from 'yup';

import { Formik, FieldArray } from 'formik';

import Modal from '../Modal/Modal';
import Input from '../Input';
import EditBlockQuotation from './components/EditBlockQuotation/EditBlockQuotation';
import TotalBlock from './components/TotalBlock/TotalBlock';
import animationData from '../../screens/shared/spiner_blue.json';
import StoreContext from '../../screens/shared/context/StoreContext';

const emptyService = {
  service: null,
  unit: null,
  qty: '',
  price: '',
  description: '',
};

export const queryGetServices = gql`
  query ServicesDataQuery @api(name: root) {
    services {
      id
      id
      id
      title
    }
  }
`;

const mutationRequestQuotation = gql`
  mutation sentRequestQuotationMutation($contractId: Int!, $lines: [OrderLineInputType]!, $subject: String!)
    @api(name: root) {
    createQuote(contractId: $contractId, lines: $lines, subject: $subject) {
      order {
        id
      }
    }
  }
`;

const toastParams = {
  autoClose: 3000,
  closeButton: true,
  hideProgressBar: false,
};

const ModalRequestQuotation = () => {
  const initialValues = { subject: '', services: [] };
  const history = useHistory();
  const { contract } = useContext(StoreContext);

  const { data: dataServides, loading: loadingServices } = useQuery(queryGetServices);

  const handleOpenModal = useCallback(
    (e, closeByPressBg) => {
      if (closeByPressBg && e.target.className === 'modal-bg') {
        history.location.key ? history.goBack() : history.push(history.location.pathname);
      } else if (!closeByPressBg) {
        history.location.key ? history.goBack() : history.push(history.location.pathname);
      }
    },
    [history]
  );

  const onSentEmailMutationSuccess = useCallback(() => {
    toast('Quotation requested successfully', {
      ...toastParams,
      type: toast.TYPE.SUCCESS,
    });
    handleOpenModal();
  }, [handleOpenModal]);

  const onSentEmailMutationError = useCallback((error) => {
    toast(error.message, {
      ...toastParams,
      type: toast.TYPE.ERROR,
    });
  }, []);

  const [sentRequestQuotationMutation, { loading: loadingSubmit }] = useMutation(mutationRequestQuotation, {
    onCompleted: onSentEmailMutationSuccess,
    onError: onSentEmailMutationError,
  });

  const handleSubmit = useCallback(
    (values, resetForm) => {
      const { services, subject } = values;
      const lines = services.map((item) => {
        return {
          serviceId: +item.service.id,
          unitType: item.unit.value,
          quantity: +item.qty,
          unitPrice: item.price,
          description: item.description,
        };
      });

      contract
        ? sentRequestQuotationMutation({
            variables: {
              contractId: +contract.id,
              lines: lines,
              subject: subject,
            },
          })
        : onSentEmailMutationError({ message: 'No project selected' });
    },
    [contract, onSentEmailMutationError, sentRequestQuotationMutation]
  );

  const schema = Yup.object().shape({
    subject: Yup.string().required('This field is required'),
    services: Yup.array()
      .of(
        Yup.object().shape({
          price: Yup.string().required('This field is required'),
          qty: Yup.string().required('This field is required'),
          unit: Yup.object().nullable().required('This field is required'),
          service: Yup.object().nullable().required('This field is required'),
        })
      )
      .required('Must have services'),
  });

  const formatDataForSelect = (arr) =>
    arr.map((item) => {
      return { id: item.id, label: item.title, value: item.title };
    });

  return (
    history.location.search === '?request_quotation' && (
      <Modal>
        <Formik
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            handleSubmit(values, setSubmitting, initialValues, resetForm);
          }}
        >
          {({ handleSubmit, handleChange, handleBlur, setFieldValue, values, errors }) => (
            <form onSubmit={handleSubmit}>
              <div
                className="modal-bg"
                data-testid="modal-request-quotation"
                onClick={(e) => handleOpenModal(e, 'close')}
              >
                <div className="modal-request-quotation">
                  <div className="modal__build-head">
                    <h2>
                      <a
                        className="modal__build-head-link"
                        href={`${history.location.pathname}?request_quotation`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        New quotation request
                      </a>
                    </h2>
                    <span className="icon-close" onClick={handleOpenModal}  data-testid="close-quotation"/>
                  </div>
                  <div className="modal__build-info modal_body">
                    <div className="modal__content">
                      <Input
                        variant="textarea"
                        helper={errors.subject}
                        helperVariant={errors.subject ? 'error' : 'message'}
                        placeholder="Add some details"
                        label="Subject"
                        id="subject"
                        callback={handleChange('subject')}
                        fieldProps={{
                          name: 'subject',
                          value: values.subject,
                          placeholder: 'Enter request subject',
                          onBlur: handleBlur('subject'),
                        }}
                      />
                      <div className="modal__title-block">
                        <div className="modal__title-block--row">
                          <div className="modal__title-block--middle">
                            <span>Service</span>
                          </div>
                          <div className="modal__title-block--small">
                            <span>Unit</span>
                          </div>
                          <div className="modal__title-block--small">
                            <span>QTY</span>
                          </div>
                          <div className="modal__title-block--small">
                            <span>Unit Price</span>
                          </div>
                          <div className="modal__title-block--small">
                            <span>Subtotal</span>
                          </div>
                        </div>
                      </div>
                      {loadingServices ? (
                        <Lottie
                          data-testid="tab_expenses_history"
                          className="lottie"
                          loop={true}
                          play={true}
                          animationData={animationData}
                          width={100}
                          style={{ width: 100, height: 100, margin: '0px auto' }}
                        />
                      ) : (
                        <FieldArray
                          name="services"
                          render={(arrayHelpers) => (
                            <>
                              {typeof errors.services === 'string' && (
                                <div className="error-services">{errors.services}</div>
                              )}

                              {values.services.map((service, index, arr) => (
                                <EditBlockQuotation
                                  key={index}
                                  service={service}
                                  index={index}
                                  errors={errors}
                                  handleChange={handleChange}
                                  setFieldValue={setFieldValue}
                                  arrayHelpers={arrayHelpers}
                                  servicesOptions={formatDataForSelect(dataServides.services)}
                                />
                              ))}
                              <TotalBlock
                                arrayHelpers={arrayHelpers}
                                errors={errors}
                                values={values}
                                emptyService={emptyService}
                              />
                            </>
                          )}
                        />
                      )}
                    </div>
                  </div>
                  <div className="modal__footer">
                    <button
                      type="button"
                      className="modal__button modal__button--outlined"
                      onClick={handleOpenModal}
                      data-testid="cancel-request"
                    >
                      Cancel
                    </button>
                    <div className="modal__submit-block" data-testid="send-request">
                      <button
                        type="submit"
                        disabled={loadingSubmit || !_.isEmpty(errors)}
                        className="modal__button modal__button--contained"
                      >
                        Send request
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          )}
        </Formik>
      </Modal>
    )
  );
};

export default ModalRequestQuotation;
